import { useEffect, useRef, useState } from 'react';
import { Dropdown } from 'react-bootstrap';
import { CommunicationService } from '@propertelligent/client-api';
import EmptyState from '../../../../newComponents/emptystate';
import CustomTooltip from '../../../common/CustomTooltip';
import { GetImages } from '../../../../utils/GetImages';
import Compose from './Compose';
import apiHelper from '../../../../utils/apiHelper';
import NameAvatarComponent from '../../../../newComponents/NameAvatar/NameAvatar';
import { TextCss } from '../../../GlobalCss/TextCss';
import { Colors } from '../../../GlobalCss/Colors';
import { EmailBodyIframe, getEmailDisplayContent } from './EmailBodyIframe';
import { formatLocalDisplayDateFull } from '../personalEmailApi';
import SimpleSpinner from '../../../loader';

const ShowEmail = ({ emailId, onGetEmailList, setEmailId }) => {
  const [emailMessage, setEmailMessage] = useState([])
  const [important, setImportant] = useState(false)
  const [replyId, setReplyId] = useState<String>()
  const [replytype, setReplyType] = useState<String>("")
  const [forwardBody, setForwardBody] = useState("");
  // Shown only while actually switching to a different email (see the emailId/important effect below) -- so
  // marking a message important/unimportant just refreshes it in place without flashing the loader.
  const [loading, setLoading] = useState(false);
  const scrollDown = useRef(null);
  const scrollContainerRef = useRef(null);
  const messagesContainerRef = useRef(null);
  // Set when fetching the thread fails, so the pane can show something other than a silent blank area.
  const [fetchError, setFetchError] = useState(false);
  // Tracks the most recently requested emailId so a slower in-flight fetch can detect it's been superseded by
  // a faster click on a different message and bail out instead of painting a stale thread under the current
  // selection (mirrors ShowOutlookMessage.tsx's latestUidRef).
  const latestEmailIdRef = useRef(null);
  // Distinguishes "emailId actually changed" (show the loader, clear the old thread) from "only `important`
  // changed" (same emailId -- just refresh the already-displayed thread in place).
  const prevEmailIdRef = useRef<any>(undefined);

  useEffect(() => {
    if (replyId && scrollDown.current && scrollContainerRef.current) {
      const scrollReplyIntoView = () => {
        scrollDown.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
      };

      // Observing scrollDown.current itself is a no-op -- it's an empty placeholder div with no children of
      // its own, so its subtree never mutates. The actual height change that needs to re-trigger the scroll
      // happens earlier in the tree: EmailBodyIframe sets the iframe's `height` attribute once the message's
      // (possibly large, image-heavy) content finishes loading, which can land well after this effect's
      // one-shot 200ms timeout -- leaving the reply box only partially scrolled into view, appearing to sit
      // "behind" the still-visible tail of the message (bug/email-date-display). Observe only the read-only
      // messages area (messagesContainerRef), not the whole composeContainer -- that would also cover the
      // live reply editor below it, re-triggering this scroll on every keystroke/Enter inside the reply box.
      const observer = messagesContainerRef.current
        ? new MutationObserver(scrollReplyIntoView)
        : null;
      observer?.observe(messagesContainerRef.current, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['height', 'style'],
      });

      const timeouts = [200, 600, 1200].map((delay) => setTimeout(scrollReplyIntoView, delay));

      return () => {
        observer?.disconnect();
        timeouts.forEach(clearTimeout);
      };
    }
  }, [replyId, emailMessage]);


  useEffect(() => {
    if (emailMessage?.length) {
      setReplyId(null);
    }
  }, [emailId, emailMessage?.length]);
  const OnDeleteEmail = (id) => {
    if (emailId !== id) {
      apiHelper(CommunicationService.deleteApiGmailDeleteEmailMessage, { successMessage: "Delete Email", failureMessage: "Unable to delete email", showNotification: true }, id)
        .then((res) => {
          getEmailMessages()
          onGetEmailList()
        }).catch((error) => { });
    } else
      apiHelper(CommunicationService.deleteApiGmailDeleteEmailThread, { successMessage: "Delete Email", failureMessage: "Unable to delete email", showNotification: true }, emailId)
        .then((res) => {
          setEmailId(null)
          getEmailMessages()
          onGetEmailList()
        }).catch((error) => { })
  }
  const onForwardEmail = (item) => {
    setReplyId(item.id);
    setReplyType("forward");
    setForwardBody(item.body);
  };

  const OnMakeImportant = (isImportant, id) => {
    if (emailId !== id) {
      let da = isImportant ? false : true

      setImportant(da)
      apiHelper(CommunicationService.postApiGmailToggleMessageImportance, { showNotification: false }, id, da)
        .then((res) => {
          getEmailMessages()
          onGetEmailList()
        }).catch((error) => { })
    }
    let da = isImportant ? false : true

    setImportant(da)
    apiHelper(CommunicationService.postApiGmailToggleThreadImportance, { showNotification: false }, id, da)
      .then((res) => {
        getEmailMessages()
        onGetEmailList()
      }).catch((error) => { })
  }

  const getEmailMessages = () => {
    const requestedEmailId = emailId;
    setFetchError(false);
    apiHelper(CommunicationService.getApiGmailEmailThread, { showNotification: false }, emailId)
      .then((res) => {
        if (latestEmailIdRef.current !== requestedEmailId) return; // superseded by a newer selection
        const allresults: any = res?.data?.success;
        setEmailMessage(allresults?.messages ?? []);
      }).catch((error) => {
        // A failed fetch previously left the pane permanently blank (the loader clears but emailMessage stays
        // []) with no indication anything went wrong -- surface it instead (bug/email-date-display).
        if (latestEmailIdRef.current !== requestedEmailId) return;
        setEmailMessage([]);
        setFetchError(true);
      })
      .finally(() => {
        if (latestEmailIdRef.current === requestedEmailId) setLoading(false);
      });
  }

  useEffect(() => {
    const isSwitchingEmail = prevEmailIdRef.current !== emailId;
    prevEmailIdRef.current = emailId;
    latestEmailIdRef.current = emailId;

    if (!emailId) {
      setEmailMessage([]);
      setLoading(false);
      setFetchError(false);
      return;
    }
    if (isSwitchingEmail) {
      // Clear the previous email's content immediately so it can't keep showing on the right while the new
      // one is still loading -- the loader takes its place until the fetch resolves.
      setEmailMessage([]);
      setLoading(true);
    }
    getEmailMessages();
  }, [emailId, important]);

  const data = [
    {
      id: 1,
      img: GetImages.EmailForward,
      name: "Forward",
      linkpath: undefined
    },
    {
      id: 2,
      img: GetImages.BlackDelete,
      name: "Delete",
      linkpath: undefined
    },
  ]


  return (
    <>
      <div className="composeContainer" ref={scrollContainerRef}>
        {emailId == null ? (
          <EmptyState
            bodytext="Click on the chat from left to start the conversation"
            isButton={false}
            isBorder={false}
          />
        ) : loading ? (
          <div className="spinnerContainer">
            <SimpleSpinner />
          </div>
        ) : fetchError ? (
          <EmptyState
            bodytext="Unable to load this email. Please try again."
            isButton={false}
            isBorder={false}
          />
        ) : (<>
          <div ref={messagesContainerRef} style={{ width: '100%' }}>
          {emailMessage?.map((item, index) => (
            <div className="ShowEmailSection" key={item?.id ?? index}>
              <div className={
                emailMessage?.length - 1 === index
                  ? ` gap-4 ShowEmailSectionInner`
                  : ` gap-4 ShowEmailSectionInner BorderBottom`
              } >
                <div className="userIcon ">
                  <NameAvatarComponent
                    firstName={item.fromName.split(" ")?.length > 0 &&
                      item.fromName.split(" ")[0]}
                    lastName={item.fromName.split(" ")?.length > 1
                      ? item.fromName.split(" ")[1]
                      : ""}
                    heightWidth={40}
                    shape="square"
                    className={TextCss.heading_700_16}
                    color={Colors.blue.regular}
                    backgroundColor={Colors.blue.lighter}
                  />

                </div>
                <div className="email_Deatils_section">
                  <div className="email_details_header ">
                    <div className="email_sender_detials ">
                      <p className="sub_heading text-start">
                        {item?.fromName}
                      </p>
                      <p className="sub_text  grey_subtext1">
                        {formatLocalDisplayDateFull(item?.date)}
                      </p>
                    </div>
                    <div className="email_action ">
                      <CustomTooltip children={<img
                        src={
                          !item?.isImportant
                            ? GetImages.BlueStar
                            : GetImages.FilledBlueStar
                        }
                        onClick={() =>
                          OnMakeImportant(item?.isImportant, item.id)
                        }
                        className="iconstyle"
                        alt="Important"
                      />}
                        content={"Marked as important"} />

                      <img
                        src={GetImages.BlueReply}
                        onClick={() => {
                          setReplyId(item.id);
                          setReplyType("reply");
                        }}
                        className="iconstyle"
                        alt="Reply"
                      />

                      <Dropdown>
                        <Dropdown.Toggle
                          style={{ outline: "none" }}
                          variant="none"
                          // id="filterDropdown"
                          className="m-0 p-0"
                        >
                          <img
                            src={GetImages.BlueMore}
                            className="iconstyle"
                            alt="More"
                          />

                        </Dropdown.Toggle>
                        <Dropdown.Menu className="m-2">
                          {data?.map((it, index) => (
                            <>
                              <Dropdown.Item
                                key={it.id}
                                onClick={() =>
                                  it.id == 2
                                    ? OnDeleteEmail(item.id)
                                    : onForwardEmail(item)
                                }
                              >
                                <div className="text-decoration-none d-flex flex-row align-items-center text-dark gap-2">
                                  {it.img && (
                                    <img
                                      src={it.img}
                                      width={20}
                                      height={20}
                                      alt="Black Email Icon"
                                    />
                                  )}
                                  <p className="sub-text">{it.name}</p>
                                </div>
                              </Dropdown.Item>
                              {index !== data.length - 1 && (
                                <Dropdown.Divider />
                              )}
                            </>
                          ))}
                        </Dropdown.Menu>
                      </Dropdown>
                    </div>
                  </div>
                  <p className="normal_text grey_subtext1">{item?.subject}</p>
                  <EmailBodyIframe
                    body={getEmailDisplayContent(item)}
                    onHeightChange={
                      emailMessage?.length - 1 === index && replyId
                        ? () => scrollDown.current?.scrollIntoView({ behavior: 'smooth', block: 'end' })
                        : undefined
                    }
                  />
                </div>
              </div>
            </div>
          ))}
          </div>

          {replyId && (
            <>
              {/* <div className="w-100 BorderTop mt-1 ShowEmailSection"> */}
              <div className="compose-box-fixed mt-3">
                <Compose
                  getEmailMessages={getEmailMessages}
                  emailId={emailId}
                  replyId={replyId}
                  setReplyId={setReplyId}
                  replytype={replytype}
                  forwardBody={forwardBody} compose={undefined} setcompose={undefined} />
                <div ref={scrollDown} />
              </div>
            </>
          )}
        </>
        )}
      </div>
    </>

  );
}
export default ShowEmail